import { useEffect, useRef } from 'react';
import type { FormInstance } from 'antd';
import { useAuthStore } from '@/store/authStore';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';

/**
 * User master default_department_id for the active session company.
 * Returns undefined if unset, inactive, or not in the selection list (wrong company).
 */
export function resolveUserDefaultDepartmentId(
  defaultDepartmentId: string | null | undefined,
  selectableDepartmentIds: Iterable<string>
): string | undefined {
  const id = String(defaultDepartmentId ?? '').trim();
  if (!id) return undefined;
  const set =
    selectableDepartmentIds instanceof Set
      ? selectableDepartmentIds
      : new Set(selectableDepartmentIds);
  return set.has(id) ? id : undefined;
}

export function applyDefaultDepartment(
  form: FormInstance,
  options: {
    fieldName?: string;
    defaultDepartmentId?: string | null;
    selectableDepartmentIds: Iterable<string>;
    /** When true (default), do not overwrite an already-set form value. */
    skipIfSet?: boolean;
  }
): string | undefined {
  const fieldName = options.fieldName ?? 'department_id';
  const skipIfSet = options.skipIfSet !== false;
  if (skipIfSet) {
    const current = form.getFieldValue(fieldName);
    if (String(current ?? '').trim()) return undefined;
  }
  const resolved = resolveUserDefaultDepartmentId(
    options.defaultDepartmentId,
    options.selectableDepartmentIds
  );
  if (!resolved) return undefined;
  form.setFieldsValue({ [fieldName]: resolved });
  return resolved;
}

/** Hook: resolved default for active company, or undefined. */
export function useUserDefaultDepartmentId(): string | undefined {
  const defaultId = useAuthStore((s) => s.user?.default_department_id);
  const { data: departments = [] } = useDepartmentsForSelection();
  return resolveUserDefaultDepartmentId(
    defaultId,
    departments.map((d) => d.id)
  );
}

/**
 * Prefill department_id once on create when empty (parent inheritance / user choice wins).
 * Resets the one-shot flag when `enabled` goes false (e.g. dialog closed).
 */
export function useApplyUserDefaultDepartment(
  form: FormInstance,
  enabled: boolean,
  fieldName = 'department_id'
): void {
  const defaultDepartmentId = useUserDefaultDepartmentId();
  const appliedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      appliedRef.current = false;
      return;
    }
    if (appliedRef.current || !defaultDepartmentId) return;
    const current = form.getFieldValue(fieldName);
    if (String(current ?? '').trim()) {
      appliedRef.current = true;
      return;
    }
    form.setFieldsValue({ [fieldName]: defaultDepartmentId });
    appliedRef.current = true;
  }, [enabled, defaultDepartmentId, form, fieldName]);
}
